/**
 * What pulls on the ship: every registry row with a mass, all the time. The
 * sun always, plus every planet and moon, each weighted by its own GM and
 * distance and summed - nothing is dropped.
 *
 * `src/main.ts` used to hold the per-slot arrays below as its own module
 * state; one `createGravityField()` call, made once, owns them instead. Every
 * array and struct property is the same reference for the life of the
 * process - grown by `.push()`, never reassigned - so a caller that
 * destructures the handle once keeps seeing every later write.
 * `frameParentId`, `frameParentIdx` and `pullCount` are scalars a copy would
 * go stale on, so those are accessors instead.
 */

import type { BodyPlace, BodyPlaces } from './bodies';
import type { CentreSampler } from './flightCentres';
import type { FlightField, FlightPull, FlightVec } from './flight';
import {
  createContact, type FlightGround, type SpinRateReader, type SpinTurner, type SurfaceReader,
} from './ground.ts';
import { GRAVITATIONAL_CONSTANT_M3_KG_S2 } from './constants.ts';
import type { BodyRow } from '../world/types';
import { DAYS_PER_CENTURY, heliocentric } from '../world/ephemeris.ts';

export interface GravityFieldDeps {
  /** The live per-frame body-registry placements, or null before boot. */
  simBodies: () => BodyPlaces | null;
  /** The ship's own heliocentric place this fill starts from. */
  shipAt: () => FlightVec;
  /** The registry rows, for `heliocentric()`'s own parent chain. */
  bodyRows: () => readonly BodyRow[];
  /** The epoch the current frame ends on, and the one before it. */
  seedJd: () => number;
  seedJdPrev: () => number;
  /** The bound the sweep meets before it pays for a surface reading: the
   *  shape's own past the datum on a shape-model body, the datum on
   *  everything else. `outerRadiusM` in `src/main.ts`. */
  outerRadiusM: (row: BodyRow) => number;
  /** `FlightGround.surfaceAt`: the drawn ground under one direction out of a
   *  slot's centre. `slotSurfaceRadiusM` in `src/main.ts`. */
  surfaceAt: SurfaceReader;
  /** `FlightGround.spinBy`: a resting ship's offset turned with the slot's
   *  own spin. `slotSpinBy` in `src/main.ts`. */
  spinBy: SpinTurner;
  /** `FlightGround.spinRateAt`: the same spin as `spinBy`, as an angular
   *  velocity vector. `slotSpinRate` in `src/main.ts`. Optional so a caller
   *  with nothing to spin by need not supply one. */
  spinRateAt?: SpinRateReader | null;
  /** False on `?nogravity=1`: every pull off. */
  gravityOn: boolean;
  /** True on `?onebody=1`: the pre-fix primary-plus-hardest selection. */
  oneBody: boolean;
  /** True on `?flighthelioframe=1`: the frame never picks a parent. */
  helioFrame: boolean;
}

export interface GravityField {
  /** The ship's own place and the pulling set, in the caller's frame. Read,
   *  never written from outside `update`/`refill`/`readPulls`. */
  readonly field: FlightField;
  /** The surfaces this step may meet, over the same slots as `field.pulls`. */
  readonly ground: FlightGround;
  readonly pulls: readonly FlightPull[];
  readonly pullIds: readonly string[];
  readonly pullRadiiM: readonly number[];
  readonly pullRows: ReadonlyArray<BodyRow | null>;
  readonly pullPeriodS: readonly number[];
  readonly pullParentIdx: readonly number[];
  /** Slots the last fill actually filled. The rest of `pulls` carries a `gm`
   *  of 0. */
  readonly pullCount: number;
  /** The frame the ship's own velocity is expressed in: the body pulling
   *  hardest, or null for an inertial frame. Read by every rebase and every
   *  reader that labels what is on screen; written by `update`/`refill` and
   *  by `adoptFlightFrame` in `src/main.ts` on a seed or a park. */
  frameParentId: string | null;
  /** The same pick as a `pulls` slot index, or -1. */
  readonly frameParentIdx: number;
  /** This tick's pulling set AND the frame it is integrated in: fills `pulls`,
   *  picks the dominant body, and fills `field.frameAccel` and the ground
   *  velocities off that pick. The frame's own path calls this once a tick. */
  update(): void;
  /** The pulling set at the ship, with the frame's own pick left where it is. */
  readPulls(): void;
  /** The pulling set again, at the epoch the registry has just advanced to,
   *  with the pick the frame's START configuration made left standing. */
  refill(): void;
  /** Which of `pulls` is strongest at a point, or -1 when nothing pulls. */
  pickDominant(at: FlightVec): number;
  /** How far out a row still out-pulls the one it orbits, metres. Infinity for
   *  the primary; 0 for a row with no mass or no place. */
  holdRadiusM(bodyId: string): number;
  /** How fast a row sweeps its own orbit about the one it orbits, radians a
   *  second. Zero for the primary or a row with no place. */
  meanMotionRadS(bodyId: string): number;
  /** `FlightFrameContext.sample`: one slot's place and velocity at an instant
   *  inside the frame. */
  sampleCentre: CentreSampler;
  /** `FlightFrameContext.frameAccelAt`: the chosen parent's own acceleration
   *  from every other pull, or none. */
  frameAccelAt(bestIdx: number): void;
  /** The registry's places, snapshotted for `sampleCentre`'s previous-epoch
   *  fast path. `src/main.ts`'s `advanceSimBodies()` calls this once a frame,
   *  before `simBodies.update()` overwrites the places it reads. */
  snapshotPrev(places: readonly BodyPlace[]): void;
}

export function createGravityField(deps: GravityFieldDeps): GravityField {
  const pulls: FlightPull[] = [];
  const pullIds: string[] = [];
  // One `radius_m` per slot, beside the id: where the datum is. On a
  // shape-model body the pilot panel, the sweep and the trajectory's line all
  // read the drawn ground instead, which on Phobos stands 2.9 km either side of
  // this; the line ends on it only under `?trajdatumground=1`.
  const pullRadiiM: number[] = [];
  // The radius nothing of the body's DRAWN shape stands past, per slot: the
  // same `radius_m` on a body drawn as its datum sphere, and the shape's own
  // bound on one baked from a shape model. The ground sweep meets this before
  // it pays for a surface reading, so a sphere costs exactly what it always
  // did.
  const pullOuterRadiiM: number[] = [];
  // Each slot's heliocentric velocity, and its velocity in the frame flight
  // integrates in; a contact sets the ship's velocity to the body's own.
  const pullVel: FlightVec[] = [];
  const pullVelIn: FlightVec[] = [];
  // The row behind each slot, its own orbital period in sim seconds, and the
  // slot its parent sits in. The prediction reads all three: it sizes one
  // sampling grid per body from that body's own period and lets a body the
  // grid cannot resolve ride its parent (`src/sim/trajectory.ts`).
  const pullRows: Array<BodyRow | null> = [];
  const pullPeriodS: number[] = [];
  const pullParentIdx: number[] = [];
  let pullCount = 0;
  // The registry's places one frame ago, snapshotted by `snapshotPrev` before
  // `simBodies.update()` overwrites them. `sampleCentre` returns these for
  // `seedJdPrev` and `simBodies.places` itself for `seedJd`, so laying a
  // frame's knot grid costs no ephemeris call for either endpoint - the frame
  // already placed both.
  let prevPos = new Float64Array(0);
  let prevVel = new Float64Array(0);
  // The count `fillOrbits` last computed periods and parents for, so a second
  // and third fill in the same frame can skip the work: -1 forces a recompute,
  // and `ensureSlots` resets it whenever the slot count grows.
  let pullOrbitsCount = -1;
  let frameParentId: string | null = null;
  let frameParentIdx = -1;
  const frameAccelVec: FlightVec = { x: 0, y: 0, z: 0 };

  const ground: FlightGround = {
    radiiM: pullRadiiM, surfaceAt: deps.surfaceAt, spinBy: deps.spinBy,
    spinRateAt: deps.spinRateAt ?? null,
    outerRadiiM: pullOuterRadiiM,
    ids: pullIds, velIn: pullVelIn, contact: createContact(),
    centreAt: null, tau0: 0, centreParent: -1,
  };
  const field: FlightField = {
    at: { x: 0, y: 0, z: 0 }, pulls, ground,
  };

  function ensureSlots(n: number): void {
    if (pulls.length >= n) return;
    while (pulls.length < n) {
      pulls.push({ gm: 0, at: { x: 0, y: 0, z: 0 } });
      pullIds.push('');
      pullRadiiM.push(0);
      pullOuterRadiiM.push(0);
      pullVel.push({ x: 0, y: 0, z: 0 });
      pullVelIn.push({ x: 0, y: 0, z: 0 });
      pullRows.push(null);
      pullPeriodS.push(0);
      pullParentIdx.push(-1);
    }
    field.pulls = pulls;
    pullOrbitsCount = -1;
  }

  function copyVec(from: FlightVec, to: FlightVec): void {
    to.x = from.x;
    to.y = from.y;
    to.z = from.z;
  }

  /**
   * Every slot's velocity in the frame the flight state is integrated in: its
   * own heliocentric velocity less the frame parent's. The parent's own slot
   * reads zero, which is what a ship resting on the body it is flying in the
   * frame of has to end up with.
   */
  function fillGroundVel(parentIdx: number): void {
    const px = parentIdx >= 0 ? pullVel[parentIdx]?.x ?? 0 : 0;
    const py = parentIdx >= 0 ? pullVel[parentIdx]?.y ?? 0 : 0;
    const pz = parentIdx >= 0 ? pullVel[parentIdx]?.z ?? 0 : 0;
    for (let i = 0; i < pullVelIn.length; i++) {
      const v = pullVel[i]!;
      const o = pullVelIn[i]!;
      o.x = v.x - px;
      o.y = v.y - py;
      o.z = v.z - pz;
    }
  }

  /**
   * A row's own orbital period, sim seconds, off the mean longitude's secular
   * rate. Zero where the row has no orbit at all, which is the Sun.
   */
  function rowPeriodS(row: BodyRow): number {
    const rate = row.orbit?.rates.L_deg_cy ?? 0;
    if (!(Math.abs(rate) > 0)) return 0;
    return (360 / Math.abs(rate)) * DAYS_PER_CENTURY * 86400;
  }

  /** The period and the parent slot behind every filled slot, in one pass. */
  function fillOrbits(count: number): void {
    pullCount = count;
    if (count === pullOrbitsCount) return;
    for (let i = 0; i < count; i++) {
      const row = pullRows[i];
      if (row === null || row === undefined) {
        pullPeriodS[i] = 0;
        pullParentIdx[i] = -1;
        continue;
      }
      pullPeriodS[i] = rowPeriodS(row);
      let parent = -1;
      if (row.parent !== null) {
        for (let j = 0; j < count; j++) {
          if (pullIds[j] === row.parent) { parent = j; break; }
        }
      }
      pullParentIdx[i] = parent;
    }
    pullOrbitsCount = count;
  }

  /**
   * This tick's pulling set, into `field`. One pass over the registry's own
   * places: every row with a mass fills one slot, unless `oneBody` asks for
   * the pre-fix primary-plus-hardest pair instead. A `gm` of 0 is a slot
   * nothing filled; this function does not sort.
   */
  function fill(): void {
    const at = field.at;
    const shipAt = deps.shipAt();
    at.x = shipAt.x;
    at.y = shipAt.y;
    at.z = shipAt.z;
    for (const p of pulls) p.gm = 0;
    pullCount = 0;
    const simBodies = deps.simBodies();
    if (!deps.gravityOn || simBodies === null) return;
    ensureSlots(simBodies.places.length);
    let count = 0;
    if (deps.oneBody) {
      for (const place of simBodies.places) {
        if (place.row.parent !== null) continue;
        const gm = GRAVITATIONAL_CONSTANT_M3_KG_S2 * place.row.mass_kg;
        if (!(gm > 0)) continue;
        const slot = pulls[count]!;
        slot.gm = gm;
        slot.at.x = place.world.x;
        slot.at.y = place.world.y;
        slot.at.z = place.world.z;
        pullIds[count] = place.row.id;
        pullRadiiM[count] = place.row.radius_m;
        pullOuterRadiiM[count] = deps.outerRadiusM(place.row);
        copyVec(place.velocity, pullVel[count]!);
        pullRows[count] = place.row;
        count++;
        break;
      }
      let hardest = 0;
      let hardestIdx = -1;
      for (let i = 0; i < simBodies.places.length; i++) {
        const place = simBodies.places[i]!;
        if (place.row.parent === null) continue;
        const gm = GRAVITATIONAL_CONSTANT_M3_KG_S2 * place.row.mass_kg;
        if (!(gm > 0)) continue;
        const dx = place.world.x - at.x;
        const dy = place.world.y - at.y;
        const dz = place.world.z - at.z;
        const r2 = dx * dx + dy * dy + dz * dz;
        if (!(r2 > 0)) continue;
        const accel = gm / r2;
        if (accel <= hardest) continue;
        hardest = accel;
        hardestIdx = i;
      }
      if (hardestIdx >= 0) {
        const place = simBodies.places[hardestIdx]!;
        const slot = pulls[count]!;
        slot.gm = GRAVITATIONAL_CONSTANT_M3_KG_S2 * place.row.mass_kg;
        slot.at.x = place.world.x;
        slot.at.y = place.world.y;
        slot.at.z = place.world.z;
        pullIds[count] = place.row.id;
        pullRadiiM[count] = place.row.radius_m;
        pullOuterRadiiM[count] = deps.outerRadiusM(place.row);
        copyVec(place.velocity, pullVel[count]!);
        pullRows[count] = place.row;
        count++;
      }
      fillOrbits(count);
      return;
    }
    for (const place of simBodies.places) {
      const gm = GRAVITATIONAL_CONSTANT_M3_KG_S2 * place.row.mass_kg;
      if (!(gm > 0)) continue;
      const slot = pulls[count]!;
      slot.gm = gm;
      slot.at.x = place.world.x;
      slot.at.y = place.world.y;
      slot.at.z = place.world.z;
      pullIds[count] = place.row.id;
      pullRadiiM[count] = place.row.radius_m;
      pullOuterRadiiM[count] = deps.outerRadiusM(place.row);
      copyVec(place.velocity, pullVel[count]!);
      pullRows[count] = place.row;
      count++;
    }
    fillOrbits(count);
  }

  /**
   * Which of `pulls` is strongest at a point, or -1 when nothing pulls.
   *
   * The one place that arbitration happens. The physics frame asks at the
   * position the step is about to run FROM (`field.at`), because the field
   * the substeps integrate against has to be the one the step starts in; the
   * pilot rows and the warp envelope ask at the position the frame DREW,
   * because they label what is on screen. Those name the same body for
   * anything but a ship within one frame's own travel of a dominance
   * boundary.
   */
  function pickDominant(at: FlightVec): number {
    let bestIdx = -1;
    let bestAccel = 0;
    for (let i = 0; i < pulls.length; i++) {
      const p = pulls[i]!;
      if (!(p.gm > 0)) continue;
      const dx = p.at.x - at.x;
      const dy = p.at.y - at.y;
      const dz = p.at.z - at.z;
      const r2 = dx * dx + dy * dy + dz * dz;
      if (!(r2 > 0)) continue;
      const accel = p.gm / r2;
      if (accel <= bestAccel) continue;
      bestAccel = accel;
      bestIdx = i;
    }
    return bestIdx;
  }

  /**
   * How far out a row still out-pulls the one it orbits, metres - the radius
   * inside which that body holds a ship. `gm / r^2 = gmParent / (sep - r)^2`
   * along their own line, which is the comparison `pickDominant` makes at the
   * ship's own place every frame, solved for where it flips. Infinite for the
   * primary, which nothing out-pulls; 0 for a row with no mass or no place.
   */
  function holdRadiusM(bodyId: string): number {
    const simBodies = deps.simBodies();
    if (simBodies === null) return 0;
    let place: BodyPlace | null = null;
    let parent: BodyPlace | null = null;
    for (const pl of simBodies.places) if (pl.row.id === bodyId) { place = pl; break; }
    if (place === null) return 0;
    if (place.row.parent === null) return Infinity;
    for (const pl of simBodies.places) if (pl.row.id === place.row.parent) { parent = pl; break; }
    if (parent === null) return 0;
    const gm = GRAVITATIONAL_CONSTANT_M3_KG_S2 * place.row.mass_kg;
    const gmParent = GRAVITATIONAL_CONSTANT_M3_KG_S2 * parent.row.mass_kg;
    if (!(gm > 0) || !(gmParent > 0)) return 0;
    const sepM = Math.hypot(parent.world.x - place.world.x,
      parent.world.y - place.world.y, parent.world.z - place.world.z);
    return sepM / (1 + Math.sqrt(gmParent / gm));
  }

  /**
   * How fast a row sweeps its own orbit about the one it orbits, radians a
   * second. It sets how fast that parent rewrites a conic taken about this
   * body: the tidal term the parent adds to a body-relative state integrates
   * as `(n t)^2` over the span being predicted. Zero for the primary, which
   * has nothing rewriting it, and for a row with no place or no parent mass.
   */
  function meanMotionRadS(bodyId: string): number {
    const simBodies = deps.simBodies();
    if (simBodies === null) return 0;
    let place: BodyPlace | null = null;
    let parent: BodyPlace | null = null;
    for (const pl of simBodies.places) if (pl.row.id === bodyId) { place = pl; break; }
    if (place === null || place.row.parent === null) return 0;
    for (const pl of simBodies.places) if (pl.row.id === place.row.parent) { parent = pl; break; }
    if (parent === null) return 0;
    const gmParent = GRAVITATIONAL_CONSTANT_M3_KG_S2 * parent.row.mass_kg;
    const sepM = Math.hypot(parent.world.x - place.world.x,
      parent.world.y - place.world.y, parent.world.z - place.world.z);
    if (!(gmParent > 0) || !(sepM > 0)) return 0;
    return Math.sqrt(gmParent / (sepM * sepM * sepM));
  }

  /** The chosen parent's own acceleration from every other pull, or none. */
  function frameAccelAt(bestIdx: number): void {
    frameAccelVec.x = 0;
    frameAccelVec.y = 0;
    frameAccelVec.z = 0;
    if (bestIdx < 0) {
      field.frameAccel = null;
      return;
    }
    const centre = pulls[bestIdx]!.at;
    for (let i = 0; i < pulls.length; i++) {
      if (i === bestIdx) continue;
      const p = pulls[i]!;
      if (!(p.gm > 0)) continue;
      const dx = p.at.x - centre.x;
      const dy = p.at.y - centre.y;
      const dz = p.at.z - centre.z;
      const r2 = dx * dx + dy * dy + dz * dz;
      if (!(r2 > 0)) continue;
      const k = p.gm / (r2 * Math.sqrt(r2));
      frameAccelVec.x += dx * k;
      frameAccelVec.y += dy * k;
      frameAccelVec.z += dz * k;
    }
    field.frameAccel = frameAccelVec;
  }

  /**
   * This tick's pulling set AND the frame it is integrated in.
   *
   * The frame's parent is committed here, so only the tick's own path calls
   * it: a reader that re-picked outside the tick would move `frameParentId`
   * without the caller's own rebase ever seeing the handoff, and leave the two
   * out of step until the next frame. `readPulls()` is the half a reader
   * wants.
   */
  function update(): void {
    fill();
    const bestIdx = deps.helioFrame ? -1 : pickDominant(field.at);
    frameParentId = bestIdx < 0 ? null : (pullIds[bestIdx] ?? null);
    frameParentIdx = bestIdx;
    frameAccelAt(bestIdx);
    fillGroundVel(bestIdx);
  }

  /** The pulling set at the ship, with the frame's own pick left where it is. */
  function readPulls(): void {
    fill();
  }

  /**
   * The pulling set again, at the epoch the registry has just advanced to,
   * with the pick the frame's START configuration made left standing.
   *
   * The slot is found by id rather than kept as an index because `?onebody=1`
   * fills its second slot off a fresh ranking and can name a different row
   * than the fill the pick was made against.
   */
  function refill(): void {
    fill();
    let idx = -1;
    if (frameParentId !== null) {
      for (let i = 0; i < pulls.length; i++) {
        if (pulls[i]!.gm > 0 && pullIds[i] === frameParentId) { idx = i; break; }
      }
    }
    frameParentIdx = idx;
    frameAccelAt(idx);
    fillGroundVel(idx);
  }

  /** One slot's place and velocity at an instant inside the frame, in metres. */
  function sampleCentre(i: number, jd: number, pos: FlightVec, vel: FlightVec): void {
    const row = pullRows[i] ?? null;
    if (row === null) {
      const held = pulls[i]?.at;
      pos.x = held?.x ?? 0;
      pos.y = held?.y ?? 0;
      pos.z = held?.z ?? 0;
      vel.x = 0;
      vel.y = 0;
      vel.z = 0;
      return;
    }
    // A span's grid always ends on the frame's own two epochs; both are
    // already in hand when the slot layout matches the registry's own, one to
    // one.
    const simBodies = deps.simBodies();
    const seedJd = deps.seedJd();
    if (simBodies !== null && pullCount === simBodies.places.length && jd === seedJd) {
      const place = simBodies.places[i]!;
      pos.x = place.world.x;
      pos.y = place.world.y;
      pos.z = place.world.z;
      vel.x = place.velocity.x;
      vel.y = place.velocity.y;
      vel.z = place.velocity.z;
      return;
    }
    if (pullCount === prevPos.length / 3 && jd === deps.seedJdPrev()) {
      const o = i * 3;
      pos.x = prevPos[o]!;
      pos.y = prevPos[o + 1]!;
      pos.z = prevPos[o + 2]!;
      vel.x = prevVel[o]!;
      vel.y = prevVel[o + 1]!;
      vel.z = prevVel[o + 2]!;
      return;
    }
    const st = heliocentric(row, deps.bodyRows(), jd);
    pos.x = st.position.x * 1000;
    pos.y = st.position.y * 1000;
    pos.z = st.position.z * 1000;
    vel.x = st.velocity.x * 1000;
    vel.y = st.velocity.y * 1000;
    vel.z = st.velocity.z * 1000;
  }

  function snapshotPrev(places: readonly BodyPlace[]): void {
    if (prevPos.length !== places.length * 3) {
      prevPos = new Float64Array(places.length * 3);
      prevVel = new Float64Array(places.length * 3);
    }
    for (let i = 0; i < places.length; i++) {
      const p = places[i]!;
      const o = i * 3;
      prevPos[o] = p.world.x;
      prevPos[o + 1] = p.world.y;
      prevPos[o + 2] = p.world.z;
      prevVel[o] = p.velocity.x;
      prevVel[o + 1] = p.velocity.y;
      prevVel[o + 2] = p.velocity.z;
    }
  }

  return {
    field,
    ground,
    pulls,
    pullIds,
    pullRadiiM,
    pullRows,
    pullPeriodS,
    pullParentIdx,
    get pullCount() { return pullCount; },
    get frameParentId() { return frameParentId; },
    set frameParentId(id: string | null) { frameParentId = id; },
    get frameParentIdx() { return frameParentIdx; },
    update,
    readPulls,
    refill,
    pickDominant,
    holdRadiusM,
    meanMotionRadS,
    sampleCentre,
    frameAccelAt,
    snapshotPrev,
  };
}
